import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CreateSiteGQL, UpdateSiteGQL } from '../../graphql/graphql';
import { UploadService } from '../upload/upload.service';
import { Domain, DomainsService } from '../../domains/domains.service';

@Component({
  selector: 'app-site-form',
  templateUrl: './site-form.component.html',
  styleUrls: ['./site-form.component.scss'],
  standalone: false
})
export class SiteFormComponent implements OnInit, OnChanges {
  @Input() initialData: any = null;
  @Input() isEdit = false;
  @Input() showUrl = true;
  @Input() showCancel = true;
  @Input() siteId?: string;
  @Input() siteUrl?: string;
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() success = new EventEmitter<string>();
  @Output() error = new EventEmitter<string>();

  siteForm: FormGroup;
  isLoading = false;
  availableDomains: Domain[] = [];

  constructor(
    private fb: FormBuilder,
    private createSiteGQL: CreateSiteGQL,
    private updateSiteGQL: UpdateSiteGQL,
    private uploadService: UploadService,
    private sanitizer: DomSanitizer,
    private domainsService: DomainsService,
  ) {
    this.siteForm = this.fb.group({
      url: [''],
      domainId: [''],
      name: ['', Validators.required],
      registeredName: ['', Validators.required],
      registered_number: [''],
      gtm: [''],
      logo: ['', Validators.required],
      socialImage: ['', Validators.required],
      favicon: this.fb.group({
        s16: [''],
        s32: ['', Validators.required],
        s180: [''],
        s192: [''],
        s512: ['']
      }),
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
      contact: this.fb.group({
        line1: ['', Validators.required],
        line2: [''],
        city: ['', Validators.required],
        county: [''],
        postcode: ['', Validators.required],
        country: ['']
      }),
      social: this.fb.group({
        facebook: [''],
        instagram: [''],
        linkedIn: [''],
        twitter: ['']
      }),
      contact_form: [false],
      team_modal: [false],
      team_basic: [false],
      blog_cta: [false],
      portal: [false],
      contact_page: [''],
      footer_text: ['', Validators.required],
      bannerTitleWhite: [false]
    });
  }

  ngOnInit() {
    this.updateUrlValidators();
    this.patchFormData();
    if (!this.isEdit) {
      this.loadDomains();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['showUrl'] && this.siteForm) {
      this.updateUrlValidators();
    }
    if (changes['initialData'] && this.siteForm) {
      this.patchFormData();
    }
  }

  private loadDomains() {
    this.domainsService.listIssued().subscribe({
      next: (domains) => (this.availableDomains = domains),
      error: (err) => console.error('Failed to load domains', err),
    });
  }

  private updateUrlValidators() {
    const urlControl = this.siteForm.get('url');
    const domainControl = this.siteForm.get('domainId');
    if (this.showUrl && !this.isEdit) {
      domainControl?.setValidators([Validators.required]);
      urlControl?.clearValidators();
    } else {
      domainControl?.clearValidators();
      urlControl?.clearValidators();
    }
    domainControl?.updateValueAndValidity();
    urlControl?.updateValueAndValidity();
  }

  private patchFormData() {
    if (this.initialData) {
      this.siteForm.patchValue(this.initialData);
    }
  }

  onSubmit() {
    if (this.siteForm.valid && !this.isLoading) {
      this.isLoading = true;
      const formData = this.siteForm.value;
      
      if (this.isEdit && this.siteId) {
        this.updateSite(formData);
      } else {
        this.createSite(formData);
      }
    }
  }

  private createSite(formData: any) {
    const input: any = {
      domainId: formData.domainId,
      client: this.buildClientInput(formData)
    };

    this.createSiteGQL.mutate({ variables: { createSiteInput: input } }).subscribe({
      next: (result) => {
        this.isLoading = false;
        this.success.emit('Site created successfully!');
        this.formSubmit.emit(result.data?.createSite);
      },
      error: (error) => {
        this.isLoading = false;
        this.error.emit('Failed to create site. Please try again.');
        console.error('Error creating site:', error);
      }
    });
  }

  private updateSite(formData: any) {
    const input = {
      url: this.siteUrl || '',
      client: this.buildClientInput(formData)
    };
    
    this.updateSiteGQL.mutate({ variables: { id: this.siteId!, updateSiteInput: input } }).subscribe({
      next: (result) => {
        this.isLoading = false;
        this.success.emit('Site updated successfully!');
        this.formSubmit.emit(result.data?.updateSite);
      },
      error: (error) => {
        this.isLoading = false;
        this.error.emit('Failed to update site. Please try again.');
        console.error('Error updating site:', error);
      }
    });
  }

  private buildClientInput(formData: any) {
    return {
      name: formData.name,
      registeredName: formData.registeredName,
      registered_number: formData.registered_number,
      contact_form: formData.contact_form,
      team_modal: formData.team_modal,
      team_basic: formData.team_basic,
      email: formData.email,
      phone: formData.phone,
      contact_page: formData.contact_page,
      blog_cta: formData.blog_cta,
      portal: formData.portal,
      footer_text: formData.footer_text,
      bannerTitleWhite: formData.bannerTitleWhite || false,
      gtm: formData.gtm,
      contact: formData.contact,
      logo: formData.logo ? { src: formData.logo } : null,
      favicon: formData.favicon?.s32 ? formData.favicon : null,
      socialImage: formData.socialImage ? { src: formData.socialImage } : null,
      social: formData.social
    };
  }

  isSvgMarkup(value: string): boolean {
    return value?.trim().startsWith('<');
  }

  sanitizeSvg(value: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(value);
  }

  async uploadLogo() {
    const path = `sites/${this.siteId || 'new'}/`;
    const results = await this.uploadService.pick({ path, accept: ['image/*', '!image/svg+xml'] });
    if (results.length) {
      this.siteForm.patchValue({ logo: results[0].url });
    }
  }

  onSvgFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const svg = (reader.result as string).trim();
      if (svg.startsWith('<svg') || svg.startsWith('<?xml')) {
        this.siteForm.patchValue({ logo: svg });
      } else {
        this.error.emit('Invalid SVG file.');
      }
    };
    reader.readAsText(file);
    (event.target as HTMLInputElement).value = '';
  }

  async uploadFavicon() {
    const path = `sites/${this.siteId || 'new'}/favicon/`;
    const results = await this.uploadService.pick({
      path,
      transformations: {
        crop: { aspectRatio: 1, force: true }
      }
    });
    if (results.length) {
      const handle = results[0].url.split('/').pop();
      const base = 'https://cdn.filestackcontent.com';
      this.siteForm.get('favicon')?.patchValue({
        s16: `${base}/resize=width:16,height:16,fit:crop/output=quality:90/${handle}`,
        s32: `${base}/resize=width:32,height:32,fit:crop/output=quality:90/${handle}`,
        s180: `${base}/resize=width:180,height:180,fit:crop/output=quality:90/${handle}`,
        s192: `${base}/resize=width:192,height:192,fit:crop/output=quality:90/${handle}`,
        s512: `${base}/resize=width:512,height:512,fit:crop/output=quality:90/${handle}`,
      });
    }
  }

  async uploadSocialImage() {
    const path = `sites/${this.siteId || 'new'}/social/`;
    const results = await this.uploadService.pick({
      path,
      transformations: {
        crop: { aspectRatio: 1, force: true }
      }
    });
    if (results.length) {
      const handle = results[0].url.split('/').pop();
      const base = 'https://cdn.filestackcontent.com';
      this.siteForm.patchValue({
        socialImage: `${base}/resize=width:1000,height:1000,fit:crop/output=quality:80/${handle}`
      });
    }
  }

  onCancel() {
    if (this.formCancel.observers.length > 0) {
      this.formCancel.emit();
    }
  }
}